'use client';
// ──────────────────────────────────────────────────────
// Admin Products List — Simplified raw catalog viewer
// All filter/gate/governance UI removed.
// Data path: raw paginated products from API only.
// ──────────────────────────────────────────────────────
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { listProducts, type Product } from '@/lib/admin-api';

// ── SmartTooltip — escapes overflow:hidden via position:fixed ──
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function SmartTooltip({ trigger, children, minWidth = 0 }: { trigger: React.ReactNode; children: React.ReactNode; minWidth?: number }) {
    const ref = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState<{ x: number; y: number; flip: boolean } | null>(null);

    const show = useCallback(() => {
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const spaceAbove = rect.top;
        const flip = spaceAbove < 120; // flip below if less than 120px above
        setPos({
            x: rect.right,
            y: flip ? rect.bottom + 6 : rect.top - 6,
            flip,
        });
    }, []);

    const hide = useCallback(() => setPos(null), []);

    return (
        <div ref={ref} onMouseEnter={show} onMouseLeave={hide} className="relative inline-flex">
            {trigger}
            {pos && typeof window !== 'undefined' && createPortal(
                <div
                    className="fixed z-[9999] pointer-events-none"
                    style={{
                        left: pos.x + 8,
                        top: pos.flip ? pos.y : undefined,
                        bottom: pos.flip ? undefined : `calc(100vh - ${pos.y}px)`,
                        maxWidth: 280,
                        minWidth,
                    }}
                >
                    <div
                        className="rounded-xl px-3.5 py-2.5 text-[11px] leading-relaxed shadow-xl"
                        style={{
                            background: 'rgba(26, 26, 46, 0.92)',
                            backdropFilter: 'blur(12px)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            color: '#e2e8f0',
                        }}
                    >
                        {children}
                    </div>
                </div>,
                document.body,
            )}
        </div>
    );
}

// ── Constants ──────────────────────────────────────────
const DEFAULT_LIMIT = 50 as const;
const ALLOWED_LIMITS = [50, 100, 200] as const;
function clampLimit(raw: string | null): number {
    const n = Number(raw) || DEFAULT_LIMIT;
    return ALLOWED_LIMITS.includes(n as typeof ALLOWED_LIMITS[number]) ? n : DEFAULT_LIMIT;
}

function clampPage(n: number, lastPage: number) {
    return lastPage > 0 ? Math.min(n, lastPage) : n;
}

// ── Page number generator with ellipsis ────────────────
function getPageNumbers(current: number, total: number): (number | '...')[] {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages: (number | '...')[] = [1];
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    if (start > 2) pages.push('...');
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < total - 1) pages.push('...');
    pages.push(total);
    return pages;
}

// ── Inactive reason labels ─────────────────────────────
const INACTIVE_REASON_LABELS: Record<string, string> = {
    not_in_latest_sync: 'غير موجود في آخر تحديث',
    manually_deactivated: 'تم إيقافه يدويًا',
    no_stock: 'نفذ المخزون',
    expired: 'منتهي الصلاحية',
    discontinued: 'متوقف من المصنع',
};

// ══════════════════════════════════════════════════════
// ── Main Component ────────────────────────────────────
// ══════════════════════════════════════════════════════
export default function AdminProductsPage() {
    const router = useRouter();
    const searchParams = useSearchParams();

    // ── Read URL state — page, limit, search, and filters ──
    const urlQuery = searchParams.get('query') || '';
    const urlBarcode = searchParams.get('barcode') || '';
    const urlLimit = clampLimit(searchParams.get('limit'));
    const urlPage = Math.max(1, Math.floor(Number(searchParams.get('page')) || 1));
    const urlImageStatus = searchParams.get('imageStatus') || 'all';
    const urlContentStatus = searchParams.get('contentStatus') || 'all';
    const urlStatusFilter = searchParams.get('status') || 'all';

    // ── Component state ────────────────────────────────
    const [products, setProducts] = useState<Product[]>([]);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(urlPage);
    const [limit, setLimit] = useState(urlLimit);
    const [search, setSearch] = useState(urlQuery);
    const [loading, setLoading] = useState(true);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    const lastPage = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

    // ── Selection helpers ─────────────────────────────
    const allVisibleSelected = products.length > 0 && products.every(p => selectedIds.has(p.id));
    const someVisibleSelected = products.some(p => selectedIds.has(p.id));
    const toggleSelectAll = useCallback(() => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (allVisibleSelected) {
                products.forEach(p => next.delete(p.id));
            } else {
                products.forEach(p => next.add(p.id));
            }
            return next;
        });
    }, [products, allVisibleSelected]);
    const toggleSelect = useCallback((id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) { next.delete(id); } else { next.add(id); }
            return next;
        });
    }, []);
    // Clear selection when products change
    useEffect(() => { setSelectedIds(new Set()); }, [products]);

    // ── Navigate by updating URL ───────────────────────
    const navigate = useCallback(
        (params: { page?: number; limit?: number; query?: string; barcode?: string; imageStatus?: string; contentStatus?: string; status?: string }) => {
            const sp = new URLSearchParams();
            const p = params.page ?? currentPage;
            const l = params.limit ?? limit;
            const q = params.query ?? urlQuery;
            const b = params.barcode ?? urlBarcode;
            const img = params.imageStatus ?? urlImageStatus;
            const cnt = params.contentStatus ?? urlContentStatus;
            const st = params.status ?? urlStatusFilter;
            if (p > 1) sp.set('page', String(p));
            if (l !== DEFAULT_LIMIT) sp.set('limit', String(l));
            if (q) sp.set('query', q);
            if (b) sp.set('barcode', b);
            if (img && img !== 'all') sp.set('imageStatus', img);
            if (cnt && cnt !== 'all') sp.set('contentStatus', cnt);
            if (st && st !== 'all') sp.set('status', st);
            const qs = sp.toString();
            router.push(`/admin/products${qs ? `?${qs}` : ''}`);
        },
        [currentPage, limit, urlQuery, urlBarcode, urlImageStatus, urlContentStatus, urlStatusFilter, router],
    );

    // ── Fetch products — paginated with optional filters ──
    const fetchProducts = useCallback(async () => {
        setLoading(true);
        try {
            const params: {
                query?: string; barcode?: string; page?: number; limit?: number;
                imageStatus?: string; contentStatus?: string; status?: string;
            } = { page: urlPage, limit: urlLimit };
            if (urlQuery) params.query = urlQuery;
            if (urlBarcode) params.barcode = urlBarcode;
            if (urlImageStatus !== 'all') params.imageStatus = urlImageStatus;
            if (urlContentStatus !== 'all') params.contentStatus = urlContentStatus;
            if (urlStatusFilter !== 'all') params.status = urlStatusFilter;
            const res = await listProducts(params);
            setProducts(res.items);
            setTotal(res.total);
            setCurrentPage(res.page);
            setLimit(urlLimit);
            return res.items;
        } catch (err) {
            console.error('Failed to fetch products:', err);
            return [];
        } finally {
            setLoading(false);
        }
    }, [urlPage, urlLimit, urlQuery, urlBarcode, urlImageStatus, urlContentStatus, urlStatusFilter]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    // ── Sync local search input with URL ───────────────
    useEffect(() => {
        setSearch(urlQuery);
    }, [urlQuery]);

    // ── Derived values ─────────────────────────────────
    const effectivePage = clampPage(currentPage, lastPage);
    const showFrom = total === 0 ? 0 : (effectivePage - 1) * limit + 1;
    const showTo = Math.min(effectivePage * limit, total);
    const pageNumbers = getPageNumbers(effectivePage, lastPage);

    // ── Event handlers ─────────────────────────────────
    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        navigate({ page: 1, query: search });
    };

    const handleLimitChange = (newLimit: number) => {
        navigate({ page: 1, limit: newLimit });
    };

    const handlePageChange = (newPage: number) => {
        const clamped = clampPage(newPage, lastPage);
        if (clamped !== effectivePage) navigate({ page: clamped });
    };

    const paginationBtnBase =
        'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border';
    const paginationBtnActive =
        'bg-[var(--color-accent)] text-white border-[var(--color-accent)]';
    const paginationBtnIdle =
        'bg-[var(--color-surface-elevated)] border-[var(--color-border)] text-[var(--color-foreground)] hover:border-[var(--color-accent)]';
    const paginationBtnDisabled =
        'bg-[var(--color-surface)] border-[var(--color-border)] text-[var(--color-text-muted)] cursor-not-allowed opacity-50';

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-[var(--color-foreground)]">قائمة المنتجات</h1>
                    <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                        {total} منتج في الكتالوج
                    </p>
                </div>
                <Link
                    href="/admin/products/new"
                    className="px-5 py-2.5 rounded-xl font-semibold text-sm transition-all hover:shadow-lg"
                    style={{ background: 'var(--color-accent)', color: '#ffffff' }}
                >
                    + إضافة منتج جديد
                </Link>
            </div>

            {/* Search + Per-page selector */}
            <div className="flex gap-3 items-center flex-wrap">
                <form onSubmit={handleSearch} className="flex gap-3 flex-1 min-w-0">
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="ابحث عن منتج بالاسم، العلامة التجارية، أو الباركود..."
                        className="flex-1 px-4 py-2.5 rounded-xl bg-[var(--color-surface)] border border-[var(--color-border)] text-[var(--color-foreground)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-[var(--color-accent)] transition-colors text-sm"
                    />
                    <button
                        type="submit"
                        className="px-5 py-2.5 rounded-xl bg-[var(--color-surface-elevated)] border border-[var(--color-border)] text-[var(--color-foreground)] hover:border-[var(--color-accent)] text-sm font-medium transition-colors"
                    >
                        بحث
                    </button>
                </form>

                {/* Per-page selector */}
                <div className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
                    <span>لكل صفحة:</span>
                    {ALLOWED_LIMITS.map((l) => (
                        <button
                            key={l}
                            onClick={() => handleLimitChange(l)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${limit === l
                                ? 'bg-[var(--color-accent)]/15 border-[var(--color-accent)] text-[var(--color-accent)]'
                                : 'bg-[var(--color-surface)] border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-accent)]'
                                }`}
                        >
                            {l}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── Filter Controls ───────────────────────── */}
            <div className="flex gap-6 items-center flex-wrap" dir="rtl">
                {/* Image Status */}
                <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-[var(--color-text-secondary)]" style={{ minWidth: 50 }}>🖼️ الصور:</span>
                    <div className="flex gap-1">
                        {([['all', 'الكل'], ['has_image', 'بها صور'], ['no_image', 'بدون صور']] as const).map(([val, label]) => (
                            <button
                                key={val}
                                onClick={() => navigate({ page: 1, imageStatus: val })}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                                    urlImageStatus === val
                                        ? 'bg-[var(--color-accent)]/15 border-[var(--color-accent)] text-[var(--color-accent)] shadow-sm'
                                        : 'bg-[var(--color-surface)] border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-accent)]/50'
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Content Status */}
                <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-[var(--color-text-secondary)]" style={{ minWidth: 50 }}>📝 المحتوى:</span>
                    <div className="flex gap-1">
                        {([['all', 'الكل'], ['has_content', 'مُثرى'], ['no_content', 'بدون إثراء']] as const).map(([val, label]) => (
                            <button
                                key={val}
                                onClick={() => navigate({ page: 1, contentStatus: val })}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                                    urlContentStatus === val
                                        ? 'bg-emerald-500/15 border-emerald-500/60 text-emerald-400 shadow-sm'
                                        : 'bg-[var(--color-surface)] border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-emerald-500/40'
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Status */}
                <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-[var(--color-text-secondary)]" style={{ minWidth: 50 }}>⚡ الحالة:</span>
                    <div className="flex gap-1">
                        {([['all', 'الكل'], ['active', 'شغال'], ['inactive', 'متوقف']] as const).map(([val, label]) => (
                            <button
                                key={val}
                                onClick={() => navigate({ page: 1, status: val })}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                                    urlStatusFilter === val
                                        ? 'bg-emerald-500/15 border-emerald-500/60 text-emerald-400 shadow-sm'
                                        : 'bg-[var(--color-surface)] border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-emerald-500/40'
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Clear all filters */}
                {(urlImageStatus !== 'all' || urlContentStatus !== 'all' || urlStatusFilter !== 'all') && (
                    <button
                        onClick={() => navigate({ page: 1, imageStatus: 'all', contentStatus: 'all', status: 'all' })}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-all border border-transparent hover:border-red-500/20"
                    >
                        ✕ مسح الفلاتر
                    </button>
                )}
            </div>

            {/* Products Table */}
            <div className="glass-card overflow-hidden" style={{ minHeight: 400 }}>
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-[var(--color-border)]">
                            <th className="px-3 py-3.5 w-[40px]">
                                <input
                                    type="checkbox"
                                    checked={allVisibleSelected}
                                    ref={el => { if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected; }}
                                    onChange={toggleSelectAll}
                                    className="rounded border-[var(--color-border)] accent-[var(--color-accent)] w-4 h-4 cursor-pointer"
                                />
                            </th>
                            <th className="px-5 py-3.5 text-[var(--color-text-secondary)] font-semibold w-[60px]">الصورة</th>
                            <th className="px-5 py-3.5 text-[var(--color-text-secondary)] font-semibold">اسم المنتج</th>
                            <th className="px-5 py-3.5 text-[var(--color-text-secondary)] font-semibold">الاسم (EN)</th>
                            <th className="px-5 py-3.5 text-[var(--color-text-secondary)] font-semibold">الباركود</th>
                            <th className="px-5 py-3.5 text-[var(--color-text-secondary)] font-semibold">العلامة التجارية</th>
                            <th className="px-5 py-3.5 text-[var(--color-text-secondary)] font-semibold">الفئة</th>
                            <th className="px-5 py-3.5 text-[var(--color-text-secondary)] font-semibold">الحالة</th>
                            <th className="px-5 py-3.5 text-[var(--color-text-secondary)] font-semibold">خيارات</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={9} className="text-center py-12 text-[var(--color-text-muted)]">
                                    <div className="shimmer h-4 w-48 mx-auto mb-2" />
                                    <div className="shimmer h-4 w-32 mx-auto" />
                                </td>
                            </tr>
                        ) : products.length === 0 ? (
                            <tr>
                                <td colSpan={9} className="text-center py-12 text-[var(--color-text-muted)]">
                                    ما لقينا منتجات — جرّب تغيّر البحث
                                </td>
                            </tr>
                        ) : (
                            products.map((p) => (
                                <tr
                                    key={p.id}
                                    className={`border-b border-[var(--color-border)] last:border-0 hover:bg-[var(--color-surface-elevated)]/50 transition-colors cursor-pointer ${selectedIds.has(p.id) ? 'bg-[var(--color-accent)]/5' : ''}`}
                                    style={{
                                        opacity: !p.isActive ? 0.7 : 1,
                                    }}
                                    onClick={() => router.push(`/admin/products/${p.id}`)}
                                >
                                    {/* Selection checkbox */}
                                    <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.has(p.id)}
                                            onChange={() => toggleSelect(p.id)}
                                            className="rounded border-[var(--color-border)] accent-[var(--color-accent)] w-4 h-4 cursor-pointer"
                                        />
                                    </td>
                                    <td className="px-5 py-3">
                                        {p.thumbnailUrl ? (
                                            <div className="relative" style={{ width: 40, height: 40 }}>
                                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                                <img
                                                    src={p.thumbnailUrl}
                                                    alt={p.nameAr}
                                                    className="rounded-lg"
                                                    style={{ width: 40, height: 40, objectFit: 'cover' }}
                                                    onError={(e) => {
                                                        const img = e.currentTarget;
                                                        img.style.display = 'none';
                                                        const fallback = img.nextElementSibling as HTMLElement;
                                                        if (fallback) fallback.style.display = 'flex';
                                                    }}
                                                />
                                                <div
                                                    className="rounded-lg items-center justify-center"
                                                    style={{
                                                        display: 'none',
                                                        width: 40,
                                                        height: 40,
                                                        background: 'var(--color-surface)',
                                                        border: '1px solid var(--color-border)',
                                                    }}
                                                >
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <rect x="3" y="3" width="18" height="18" rx="2" />
                                                        <line x1="4" y1="20" x2="20" y2="4" />
                                                    </svg>
                                                </div>
                                            </div>
                                        ) : (
                                            <div
                                                className="rounded-lg flex items-center justify-center"
                                                style={{
                                                    width: 40,
                                                    height: 40,
                                                    background: 'var(--color-surface)',
                                                    border: '1px solid var(--color-border)',
                                                }}
                                            >
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="3" y="3" width="18" height="18" rx="2" />
                                                    <circle cx="8.5" cy="8.5" r="1.5" />
                                                    <path d="m21 15-5-5L5 21" />
                                                </svg>
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-5 py-3 text-[var(--color-foreground)] font-medium">{p.nameAr}</td>
                                    <td className="px-5 py-3 text-[var(--color-text-secondary)]" dir="ltr">{p.nameEn || '—'}</td>
                                    <td className="px-5 py-3">
                                        <code className="text-xs px-2 py-1 rounded-lg bg-[var(--color-surface)] text-[var(--color-accent)]" dir="ltr">
                                            {p.barcode}
                                        </code>
                                    </td>
                                    <td className="px-5 py-3 text-[var(--color-text-secondary)]">{p.brand || '—'}</td>
                                    <td className="px-5 py-3 text-[var(--color-text-secondary)]">{p.category || '—'}</td>
                                    <td className="px-5 py-3">
                                        <div className="relative group inline-block cursor-default">
                                            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${p.isActive
                                                ? 'bg-green-500/10 text-green-400'
                                                : 'bg-red-500/10 text-red-400'
                                                }`}>
                                                {p.isActive ? 'شغال' : 'متوقف'}
                                            </span>
                                            {!p.isActive && p.inactiveReason && (
                                                <div
                                                    className="absolute z-50 bottom-full mb-2 right-0 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none"
                                                    style={{
                                                        background: 'rgba(26, 26, 46, 0.92)',
                                                        color: '#fff',
                                                        padding: '6px 12px',
                                                        borderRadius: '8px',
                                                        fontSize: '11px',
                                                        fontWeight: 500,
                                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                    }}
                                                >
                                                    {INACTIVE_REASON_LABELS[p.inactiveReason] || p.inactiveReason}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-5 py-3" onClick={e => e.stopPropagation()}>
                                        <div className="flex gap-2">
                                            <Link
                                                href={`/admin/products/${p.id}`}
                                                className="px-3.5 py-1.5 rounded-lg bg-[var(--color-accent)]/10 border border-[var(--color-border-accent)] text-[var(--color-accent)] hover:bg-[var(--color-accent)]/20 text-xs font-medium transition-colors inline-block"
                                            >
                                                تعديل
                                            </Link>
                                            <Link
                                                href={`/admin/products/${p.id}`}
                                                className="px-3 py-1.5 text-sm rounded-md bg-teal-50 text-teal-700 hover:bg-teal-100 transition-colors"
                                            >
                                                عرض
                                            </Link>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* ── Batch Action Bar ───────────────────────── */}
            {selectedIds.size > 0 && (
                <div
                    className="glass-card flex items-center gap-3 flex-wrap"
                    style={{ padding: '12px 20px', borderColor: 'rgba(14, 165, 165, 0.3)' }}
                    dir="rtl"
                >
                    <span className="text-sm font-semibold" style={{ color: 'var(--color-accent)' }}>
                        ☑ {selectedIds.size} منتج محدد
                    </span>
                    {/* Clear selection */}
                    <button
                        onClick={() => setSelectedIds(new Set())}
                        className="px-2 py-1 rounded-lg text-xs text-[var(--color-text-muted)] hover:text-[var(--color-foreground)] transition-colors mr-auto"
                    >
                        ✕ إلغاء التحديد
                    </button>
                </div>
            )}

            {/* Pagination footer */}
            {!loading && total > 0 && (
                <div className="flex items-center justify-between flex-wrap gap-4">
                    {/* Showing X–Y of TOTAL */}
                    <p className="text-sm text-[var(--color-text-secondary)]">
                        عرض <span className="font-semibold text-[var(--color-foreground)]">{showFrom}</span>–<span className="font-semibold text-[var(--color-foreground)]">{showTo}</span> من <span className="font-semibold text-[var(--color-foreground)]">{total}</span> منتج
                    </p>

                    {/* Page buttons */}
                    {lastPage > 1 && (
                        <div className="flex items-center gap-1.5" dir="ltr">
                            {/* Prev */}
                            <button
                                onClick={() => handlePageChange(effectivePage - 1)}
                                disabled={effectivePage <= 1}
                                className={`${paginationBtnBase} ${effectivePage <= 1 ? paginationBtnDisabled : paginationBtnIdle}`}
                            >
                                ← السابق
                            </button>

                            {/* Numeric pages */}
                            {pageNumbers.map((p, i) =>
                                p === '...' ? (
                                    <span key={`ellipsis-${i}`} className="px-2 text-[var(--color-text-muted)] text-xs">…</span>
                                ) : (
                                    <button
                                        key={p}
                                        onClick={() => handlePageChange(p)}
                                        className={`${paginationBtnBase} ${p === effectivePage ? paginationBtnActive : paginationBtnIdle}`}
                                    >
                                        {p}
                                    </button>
                                ),
                            )}

                            {/* Next */}
                            <button
                                onClick={() => handlePageChange(effectivePage + 1)}
                                disabled={effectivePage >= lastPage}
                                className={`${paginationBtnBase} ${effectivePage >= lastPage ? paginationBtnDisabled : paginationBtnIdle}`}
                            >
                                التالي →
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
